import { Component, inject, input, OnInit, TemplateRef, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { take } from 'rxjs/operators';

import { OcJsonSchemaValidatorAjv } from '../../../../core/oc-validator/oc-jsonSchemaValidatorAjv.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IError } from '@orgacard/login';
import { IAngularMyDpOptions, IMyDateModel } from 'gramli-angular-mydatepicker';
import moment from 'moment';

import { CoreService } from '../../../../core/core.service';
import { EnvironmentService } from '../../../../core/environment.service';
import { ocValidator } from '../../../../core/oc-validator/oc.validator';
import { ModalService } from '../../../../core/services/modal.service';
import { UtilityHelperService } from '../../../../core/utility-helper.service';
import { LoadingOverlayService } from '../../../../loading-overlay/loading-overlay.service';
import { SystemMethod } from '../../../shared/models/basic.model';
import { ICertificate, ISupplierCertificate } from '../../shared/models/supplier.model';
import { SupplierService } from '../../shared/services/supplier.service';

@UntilDestroy()
@Component({
    selector: 'app-certification',
    templateUrl: './certification.component.html',
    standalone: false,
})
export class CertificationComponent implements OnInit {
    private readonly utilityHelperService = inject(UtilityHelperService);
    supplierService = inject(SupplierService);
    coreService = inject(CoreService);
    environmentService = inject(EnvironmentService);
    formBuilder = inject(UntypedFormBuilder);
    modalService = inject(ModalService);
    ocJsonSchemaValidatorAjv = inject(OcJsonSchemaValidatorAjv);
    loadingOverlayService = inject(LoadingOverlayService);

    public readonly certificationModal = viewChild<TemplateRef<any>>('certificationModal');
    public readonly certificationArchiveModal = viewChild<TemplateRef<any>>('archiveElementCertificate');

    public readonly showButtons = input<boolean>(true);
    public readonly showValid = input<boolean>(true);
    public readonly showHeadline = input<boolean>(true);

    public validFromPickerOptions: IAngularMyDpOptions = {
        appendSelectorToBody: true,
        dateRange: false,
        dateFormat: 'dd.mm.yyyy',
        disableUntil: this.coreService.convertToMyDate(moment(new Date()).subtract(1, 'days').toDate()),
    };
    public validTillPickerOptions: IAngularMyDpOptions = {
        appendSelectorToBody: true,
        dateRange: false,
        dateFormat: 'dd.mm.yyyy',
        disableUntil: this.coreService.convertToMyDate(moment(new Date()).subtract(1, 'days').toDate()),
    };
    public certificateDataChanged: boolean = true;

    public certificationForm: UntypedFormGroup;
    public possibleCertifications: ICertificate[] = [];

    public displayedCertificationsColumns: string[] = [];
    // dateModelFrom: IMyDateModel = {isRange: false, singleDate: undefined, dateRange: null};
    // dateModelTill: IMyDateModel = {isRange: false, singleDate: undefined, dateRange: null};
    // dateFormValid: boolean = false;
    public dateFromFormDateValid: boolean = false;
    public dateTillFormDateValid: boolean = false;

    public systemMethod: SystemMethod;

    public tempElementId;

    public get validFromControl(): UntypedFormControl {
        return this.certificationForm.get('validFrom') as UntypedFormControl;
    }

    public get validTillControl(): UntypedFormControl {
        return this.certificationForm.get('validTill') as UntypedFormControl;
    }

    public ngOnInit() {
        this.getSupplierCertifications();
        this.displayedCertificationsColumns = [];
        if (this.showValid()) {
            this.displayedCertificationsColumns.push('validFrom');
            this.displayedCertificationsColumns.push('validTill');
        }
        this.displayedCertificationsColumns.push('name');
        this.displayedCertificationsColumns.push('description');
        this.displayedCertificationsColumns.push('image');
        if (this.showButtons()) {
            this.displayedCertificationsColumns.push('buttons');
        }
    }

    public getSupplierCertifications() {
        if (this.supplierService.supplierId) {
            this.loadingOverlayService.startLoading();
            this.supplierService
                .getSupplierCertification()
                .pipe(take(1))
                .subscribe(
                    (data) => {
                        this.initSupplierCertifications();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
        }
    }

    public initSupplierCertifications() {
        // bereits zugeordnete ausfiltern
        // this.possibleCertifications = this.supplierService.certifications.filter(c =>
        //     this.supplierService.supplierCertifications.findIndex(sc => c.id === sc.certificate.id) === -1
        // )
        // alle
        this.possibleCertifications = this.supplierService.certifications;
    }

    public initCertificationForm(supplierCertificate?: ISupplierCertificate) {
        //
        this.certificationForm = this.formBuilder.group({
            id: supplierCertificate !== undefined ? supplierCertificate.id : undefined,
            supplierId: this.supplierService.supplierBasedata.id,
            validFrom: [
                {
                    value: supplierCertificate !== undefined && supplierCertificate.validFrom ? { jsDate: new Date(supplierCertificate.validFrom) } : undefined,
                    disabled: false, // !this.coreService.checkAbility('create', 'usermanagement.validFrom')
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierCertification',
                        'validFrom',
                        this.coreService.getMsgkey('mws.datefrom', undefined),
                    ),
                ],
            ],
            validTill: [
                {
                    value: supplierCertificate !== undefined && supplierCertificate.validTill ? { jsDate: new Date(supplierCertificate.validTill) } : undefined,
                    disabled: false, // !this.coreService.checkAbility('create', 'usermanagement.validTill')
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierCertification',
                        'validTill',
                        this.coreService.getMsgkey('mws.datetill', undefined),
                    ),
                ],
            ],
            certificate:
                supplierCertificate !== undefined
                    ? supplierCertificate.certificate.id
                    : this.possibleCertifications && this.possibleCertifications.length > 0
                      ? this.possibleCertifications[0].id
                      : undefined,
            changed: false,
        });

        this.certificationForm.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplierCertification', this.certificationForm.value);
            if (!this.certificationForm.get('changed').touched && result.isValid) {
                this.certificationForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.certificationForm.get('changed').markAsUntouched();
            }
        });
    }

    public onDateFromChanged(event: IMyDateModel) {
        // console.log('*** toggleCalendar - onDateFromChanged(): ', event);
        this.validTillPickerOptions = {
            appendSelectorToBody: true,
            dateRange: false,
            dateFormat: 'dd.mm.yyyy',
            disableUntil: this.coreService.convertToMyDate(event.singleDate.jsDate),
        };
        if (!this.certificationForm.get('validTill').value || event.singleDate.jsDate > this.certificationForm.get('validTill').value.singleDate.jsDate) {
            this.certificationForm.get('validTill').setValue(undefined);
        }

        this.certificateDataChanged = false;
    }

    public onDateTillChanged(event: IMyDateModel) {
        // console.log('*** toggleCalendar - onDateTillChanged(): ', event);
        this.certificateDataChanged = false;
    }

    public saveCertificate() {
        const tempCertificate: ICertificate = this.supplierService.certifications.find(
            (certificateElement) => certificateElement.id === this.certificationForm.get('certificate').value,
        );
        const tempSupplierCertificate: ISupplierCertificate = {
            id: this.certificationForm.get('id').value,
            supplierId: this.supplierService.supplierBasedata.id,
            validFrom: undefined,
            validTill: undefined,
            certificate: tempCertificate,
        };

        // VARIANTE date-picker Component
        if (
            this.certificationForm.get('validFrom').value &&
            this.certificationForm.get('validFrom').value.singleDate &&
            this.certificationForm.get('validFrom').value.singleDate.jsDate
        ) {
            tempSupplierCertificate.validFrom = this.certificationForm.get('validFrom').value.singleDate.jsDate;
        } else {
            tempSupplierCertificate.validFrom = undefined;
        }
        if (
            this.certificationForm.get('validTill').value &&
            this.certificationForm.get('validTill').value.singleDate &&
            this.certificationForm.get('validTill').value.singleDate.jsDate
        ) {
            tempSupplierCertificate.validTill = this.certificationForm.get('validTill').value.singleDate.jsDate;
        } else {
            tempSupplierCertificate.validTill = undefined;
        }

        this.loadingOverlayService.startLoading();
        this.supplierService
            .saveSupplierCertificate(tempSupplierCertificate)
            .pipe(take(1))
            .subscribe(
                (supplierCertificate) => {
                    if (this.systemMethod === SystemMethod.NEW) {
                        this.supplierService.supplierCertifications.push(supplierCertificate);
                        this.initSupplierCertifications();
                    } else if (this.systemMethod === SystemMethod.EDIT) {
                        const indexElement = this.supplierService.supplierCertifications.findIndex(
                            (certificate) => certificate.id === tempSupplierCertificate.id,
                        );
                        this.supplierService.supplierCertifications[indexElement] = supplierCertificate;
                    }
                    this.supplierService.supplierCertifications = this.supplierService.supplierCertifications.concat();
                    this.loadingOverlayService.stopLoading();
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
    }

    public archiveCertificate() {
        this.loadingOverlayService.startLoading();
        this.supplierService
            .deleteSupplierCertification(this.tempElementId)
            .pipe(take(1))
            .subscribe(
                (response) => {
                    const indexElement = this.supplierService.supplierCertifications.findIndex((certificate) => certificate.id === this.tempElementId);
                    this.supplierService.supplierCertifications.splice(indexElement, 1);
                    this.supplierService.supplierCertifications = this.supplierService.supplierCertifications.concat();
                    this.loadingOverlayService.stopLoading();
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
    }

    public openEditCertificateModal(supplierCertificate: ISupplierCertificate) {
        this.systemMethod = SystemMethod.EDIT;
        this.dateFromFormDateValid = true;
        this.dateTillFormDateValid = true;
        this.possibleCertifications = [];
        this.possibleCertifications.push(supplierCertificate.certificate);
        this.initCertificationForm(supplierCertificate);
        this.openModal(this.certificationModal());
    }

    public openArchiveModal(elementId: number) {
        this.tempElementId = elementId;
        this.openModal(this.certificationArchiveModal());
    }

    public openCertificateModal() {
        this.systemMethod = SystemMethod.NEW;
        this.initCertificationForm();
        this.openModal(this.certificationModal());
    }

    public openModal(modal) {
        this.modalService.openModal(modal);
    }
}
